import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GroupMember, MemberStatus } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import {
  subscribeToGroupMembers,
  approveMembership,
  denyMembership,
} from '../api/groupRepository';
import { getInitials } from './useOrganizerProfile';

export type MemberFilter = 'all' | 'active' | 'pending';

// GroupMember now includes denormalized profile data (displayName, email, photoURL)
// This type adds computed fields for convenience
export type GroupMemberWithProfile = GroupMember & {
  readonly profile: {
    readonly displayName: string;
    readonly email: string;
    readonly initials: string;
    readonly photoURL: string | null | undefined;
  };
};

export type GroupMembersState = {
  readonly members: GroupMemberWithProfile[];
  readonly filteredMembers: GroupMemberWithProfile[];
  readonly activeMembers: GroupMemberWithProfile[];
  readonly pendingMembers: GroupMemberWithProfile[];
  readonly memberCount: {
    readonly total: number;
    readonly active: number;
    readonly pending: number;
  };
  readonly loading: boolean;
  readonly error: Error | null;
  readonly approvingMemberIds: Set<string>;
  readonly denyingMemberIds: Set<string>;
  readonly approveMember: (memberId: string) => Promise<void>;
  readonly denyMember: (memberId: string) => Promise<void>;
  readonly setFilter: (filter: MemberFilter) => void;
  readonly currentFilter: MemberFilter;
};

export const useGroupMembers = (
  groupId: string | null,
  initialFilter: MemberFilter = 'all',
): GroupMembersState => {
  const { firestore } = useFirebaseServices();
  const [members, setMembers] = useState<GroupMemberWithProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(groupId));
  const [error, setError] = useState<Error | null>(null);
  const [currentFilter, setCurrentFilter] = useState<MemberFilter>(initialFilter);
  const [approvingMemberIds, setApprovingMemberIds] = useState<Set<string>>(new Set());
  const [denyingMemberIds, setDenyingMemberIds] = useState<Set<string>>(new Set());

  // Add computed fields to member (initials) - profile data is already denormalized
  const enrichMember = useCallback((member: GroupMember): GroupMemberWithProfile => {
    return {
      ...member,
      profile: {
        displayName: member.displayName,
        email: member.email,
        initials: getInitials(member.displayName),
        photoURL: member.photoURL,
      },
    };
  }, []);

  // Memoized derived state
  const { activeMembers, pendingMembers, memberCount } = useMemo(() => {
    const active = members.filter(member => member.status === 'active');
    const pending = members.filter(member => member.status === 'pending');

    return {
      activeMembers: active,
      pendingMembers: pending,
      memberCount: {
        total: members.length,
        active: active.length,
        pending: pending.length,
      },
    };
  }, [members]);

  // Filtered members based on current filter
  const filteredMembers = useMemo(() => {
    switch (currentFilter) {
      case 'active':
        return activeMembers;
      case 'pending':
        return pendingMembers;
      default:
        return members;
    }
  }, [members, activeMembers, pendingMembers, currentFilter]);

  // Optimistic member approval
  const approveMember = useCallback(
    async (memberId: string): Promise<void> => {
      if (!groupId || approvingMemberIds.has(memberId)) return;

      setApprovingMemberIds(prev => new Set(prev).add(memberId));

      try {
        // Optimistic update
        setMembers(prevMembers =>
          prevMembers.map(member =>
            member.uid === memberId
              ? { ...member, status: 'active' as MemberStatus }
              : member,
          ),
        );

        await approveMembership(firestore, groupId, memberId);
      } catch (approvalError) {
        // Revert optimistic update on error
        setMembers(prevMembers =>
          prevMembers.map(member =>
            member.uid === memberId
              ? { ...member, status: 'pending' as MemberStatus }
              : member,
          ),
        );

        console.error('Failed to approve member:', approvalError);
        setError(
          approvalError instanceof Error
            ? approvalError
            : new Error('Failed to approve member'),
        );
      } finally {
        setApprovingMemberIds(prev => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    },
    [firestore, groupId, approvingMemberIds],
  );

  // Member denial with optimistic updates
  const denyMember = useCallback(
    async (memberId: string): Promise<void> => {
      if (!groupId || denyingMemberIds.has(memberId)) return;

      setDenyingMemberIds(prev => new Set(prev).add(memberId));

      try {
        // Optimistic removal
        setMembers(prevMembers => prevMembers.filter(member => member.uid !== memberId));

        await denyMembership(firestore, groupId, memberId);
      } catch (denialError) {
        // Note: For denial, we don't revert the optimistic update as the member
        // should be removed from the list. If there's an error, the real-time
        // subscription will restore the correct state.

        console.error('Failed to deny member:', denialError);
        setError(
          denialError instanceof Error ? denialError : new Error('Failed to deny member'),
        );
      } finally {
        setDenyingMemberIds(prev => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    },
    [firestore, groupId, denyingMemberIds],
  );

  const setFilter = useCallback((filter: MemberFilter) => {
    setCurrentFilter(filter);
  }, []);

  useEffect(() => {
    if (!groupId) {
      setMembers([]);
      setLoading(false);
      setError(null);
      return () => undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToGroupMembers(
      firestore,
      groupId,
      snapshotMembers => {
        // Members already have denormalized profile data, just add computed fields
        const enrichedMembers = snapshotMembers.map(member => enrichMember(member));
        setMembers(enrichedMembers);
        setLoading(false);
      },
      subscriptionError => {
        setError(subscriptionError);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firestore, groupId, enrichMember]);

  return {
    members,
    filteredMembers,
    activeMembers,
    pendingMembers,
    memberCount,
    loading,
    error,
    approvingMemberIds,
    denyingMemberIds,
    approveMember,
    denyMember,
    setFilter,
    currentFilter,
  };
};
